import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { useChatWorkflowShared } from "@intelligems/ig-plugin/builder/workflows/execute-workflows/chat-workflow";
import type { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import type { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { IgAiIcon } from "@intelligems/ig-theme/IgAiIcon";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { aiApiClient } from "@src/api/ai-api";
import { useLinkAgenticLogicRuleAuthoringSession } from "@src/api/agentic-logic/update-agentic-logic-rule";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { agenticLogicKeys } from "@src/api/queryKeys";
import { AtlasRailHeader } from "@src/features/ai/components/AiChat/AtlasRailHeader";
import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { useBeforeUnloadGuard } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/useBeforeUnloadGuard";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import type { OnAiChatSend } from "@src/features/ai/components/WorkflowEditor/execution/types";
import { AtlasRailSuggestions } from "@src/features/ai/pages/agentic-logic/chat/AtlasRailSuggestions";
import { AtlasTurnFeedback } from "@src/features/ai/pages/agentic-logic/chat/AtlasTurnFeedback";
import { CartScenarioAsk } from "@src/features/ai/pages/agentic-logic/chat/CartScenarioAsk";
import { RailComposerCardProvider } from "@src/features/ai/pages/agentic-logic/chat/RailComposerCardProvider";
import { resolveAgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/ruleViewParams";
import { surfaceForPathname } from "@src/features/ai/pages/agentic-logic/surface/surface";
import { useActiveAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useActiveAgenticLogicSurface";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiChat,
  getAiChat,
  handleAiChatAction,
  markChatInterrupted,
} from "@src/store/slices/aiChat";
import {
  getAiChatProps,
  getAtlasActiveScenario,
  setAiChat,
} from "@src/store/slices/global";

import {
  type AgenticLogicCreationInitialContext,
  readOpeningPrompt,
} from "./agenticLogicCreationContext";
import {
  AgenticLogicCreationController,
  cancelActiveAgenticLogicCreation,
} from "./agenticLogicCreationController";
import {
  type CompletedAgenticLogicRule,
  trackAgenticLogicCreationInTranscript,
} from "./agenticLogicCreationEvents";
import { invalidateAgenticLogicWrites } from "./agenticLogicWriteInvalidation";
import {
  ATLAS_CONVERSATION_TOOLS,
  renderTemplateRuleProposalCard,
} from "./templateRuleProposalCard";
import { useStopChat } from "./hooks/useStopChat";
import { useAtlasChatLayout } from "./useAtlasChatLayout";
import { useAtlasRailCollapse } from "./useAtlasRailCollapse";
import { useAgenticLogicCreationContextBuilder } from "./useAgenticLogicCreationContext";
import { useAgenticLogicDataSurface } from "@src/features/ai/pages/agentic-logic/useAgenticLogicDataSurface";

interface AgenticLogicCreationChatProps {
  onClose?: () => void;
}

export const AgenticLogicCreationChat = ({
  onClose,
}: AgenticLogicCreationChatProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // The controller below is built once and closes over its callbacks; the
  // location must be read through a ref or the create→rule query-param
  // navigation would leave the callbacks comparing against a stale search.
  const locationRef = useRef(location);
  locationRef.current = location;
  // The surface this chat is authoring for. Registered by whichever surface
  // provider is mounted, because this component sits in the layout shell (or,
  // during a takeover, inside a dialog) and cannot read the page tree's
  // context — and a pathname cannot describe an embedded surface, whose host
  // is a dialog. Read through a ref for the same reason as the location: the
  // controller's callbacks are built once and fire much later.
  const activeSurface = useActiveAgenticLogicSurface();
  const surfaceRef = useRef(activeSurface);
  surfaceRef.current = activeSurface;
  const currentSurface = () =>
    surfaceRef.current ?? surfaceForPathname(locationRef.current.pathname);
  // Same reason as the location above: the run token has to advance from
  // whatever the store holds NOW, not from what it held when the controller
  // was built, or re-running the same cart would reuse its token and the
  // canvas would never replay the walk.
  const activeScenario = useSelector(getAtlasActiveScenario);
  const activeScenarioRef = useRef(activeScenario);
  activeScenarioRef.current = activeScenario;
  const aiChat = useSelector(getAiChat);
  const org = useGetOrg();
  const { dataSurface } = useAgenticLogicDataSurface();
  const buildCodeEntryCreationContext =
    useAgenticLogicCreationContextBuilder(dataSurface);
  const { initialContext, buildingRuleId, pendingRuleReview } = useSelector(
    getAiChatProps<AgenticLogicCreationInitialContext>
  );
  // Read by the controller's callbacks, which close over the first render.
  const initialContextRef = useRef(initialContext);
  initialContextRef.current = initialContext;
  // Background write: the rule row records which conversation authored it, so
  // the workspace can load this transcript back after a reload. The create
  // tool runs server-side and does not carry the chat's session id, so the
  // client — the one place that knows both halves — records the association as
  // soon as the rule exists.
  const linkAuthoringSession = useLinkAgenticLogicRuleAuthoringSession();
  const autoStarted = useRef(false);
  const controller = useRef<AgenticLogicCreationController | null>(null);
  // True from the moment a first message is accepted until its session
  // request is on the wire. Opening a session on a rule awaits that rule's
  // draft (see `startSession`), and during that await nothing else reports
  // a send in flight: the stream's own `isPending` only flips once the
  // request is made. Left unreported, the composer and the cart offers stay
  // live, so a second click opens a second session — and each send aborts the
  // one before it.
  const [openingSession, setOpeningSession] = useImmer(false);
  // Full screen is the new-rule conversation before there is anything to
  // build; its header leaves the conversation rather than hiding a rail.
  const layout = useAtlasChatLayout();
  const [confirmingExit, setConfirmingExit] = useImmer(false);
  const railCollapse = useAtlasRailCollapse();

  const chatWorkflow = useChatWorkflowShared(
    aiApiClient,
    (action: UiStreamAction) => {
      controller.current?.handleStreamAction(action);
    }
  );
  const stopChat = useStopChat(chatWorkflow, aiChat.sessionId);

  // How far each session's rule has been handed to the workspace, by the live
  // stream or from a restored transcript (below), so neither repeats a step.
  const handedOff = useRef(new Map<string, "created" | "completed">());
  const handOffCreatedRule = (ruleId: string, sessionId: string | null) => {
    if (sessionId) {
      handedOff.current.set(sessionId, "created");
      linkAuthoringSession.mutate({
        ruleId,
        authoringSessionId: sessionId,
      });
    }
    // The rule row exists while the chat keeps authoring its draft. Hand
    // the author to the workspace now: `buildingRuleId` is what keeps its
    // Selection logic canvas in the "being built" skeleton until the
    // draft (and its graph) land. Because the views of a surface differ
    // only by query param, this component — and the live stream — stay
    // mounted across the navigation.
    dispatch(
      setAiChat([
        { key: "buildingRuleId", value: ruleId },
        { key: "createdRuleId", value: ruleId },
      ])
    );
    void queryClient.invalidateQueries({
      queryKey: agenticLogicKeys.rules(),
    });
    navigate(currentSurface().rulePath(ruleId, "selection_logic"));
  };
  const handOffCompletedRule = (
    completedRule: CompletedAgenticLogicRule,
    sessionId: string | null
  ) => {
    // Template-built rules never fire `onRuleCreated` — they complete
    // outright — so this is where they get their transcript pointer.
    // Re-sending the id a code rule already linked above is a server-side
    // no-op, and a revision session finishing here repoints the rule at
    // this newer conversation.
    if (sessionId) {
      handedOff.current.set(sessionId, "completed");
      linkAuthoringSession.mutate({
        ruleId: completedRule.id,
        authoringSessionId: sessionId,
      });
    }
    // The workspace is usually already mounted (the create tool navigated
    // to it) sitting on a cached `{draft: null}`: the chat saved the
    // draft through a backend tool, so nothing client-side refreshed it.
    // A completed build is a rule write — the same refetches (rule, draft,
    // versions, example carts, the list) that resolve its skeleton canvas
    // into the real graph, surface the publish/repair the save kicked off,
    // and swap the cart-test card's catalog rows for the rule's own carts.
    // `buildingRuleId` is deliberately kept until the session ends — it
    // bridges the gap while these refetches land.
    invalidateAgenticLogicWrites(queryClient, [
      { kind: "rule", ruleId: completedRule.id },
    ]);
    // The chat saved the draft through a backend tool, so the mutation
    // that normally retires cached runs never ran here. Any run on the
    // canvas answered for source that has just been replaced.
    queryClient.removeQueries({
      predicate: (query) => {
        const [key] = query.queryKey as [Record<string, unknown> | undefined];
        return (
          key?.scenarioEvaluation === true && key.ruleId === completedRule.id
        );
      },
    });
    // The session is over but the conversation is not: only the entry
    // context is dropped (that is what marks a build as running and what
    // locks the create page). The panel stays open in the same mode with
    // its transcript intact, so the author can read back what Atlas did
    // while looking at the rule it produced — and, because the views of a
    // surface differ only by query param, this whole component stays
    // mounted across the navigation below rather than being torn down and
    // rebuilt empty.
    dispatch(
      setAiChat([
        // A build started from the create view (it carries an entry context)
        // made this rule; a revision on an existing rule's page did not.
        ...(initialContextRef.current
          ? ([{ key: "createdRuleId", value: completedRule.id }] as const)
          : []),
        { key: "initialContext", value: null },
        { key: "start", value: false },
      ])
    );
    // Onto the readable canvas, not the code editor: the author described
    // what the rule should do in words, so the graph is the surface that
    // answers "did it build what I asked for". The publish the rule kicks
    // off on its own is reported above the tabs, so landing here does not
    // hide it. Skipped when the create tool already navigated here — a
    // second push of the same URL would only add a dead history entry.
    const { pathname: currentPath, search } = locationRef.current;
    const target = currentSurface().rulePath(
      completedRule.id,
      "selection_logic"
    );
    if (currentPath + search !== target) {
      navigate(target);
    }
  };

  if (!controller.current) {
    controller.current = new AgenticLogicCreationController({
      createId: v4,
      dispatch: (action) => dispatch(handleAiChatAction(action)),
      mutate: chatWorkflow.mutate,
      onRuleCreated: (ruleId, sessionId) => {
        handOffCreatedRule(ruleId, sessionId);
      },
      onComplete: (completedRule, sessionId) => {
        handOffCompletedRule(completedRule, sessionId);
      },
      // Atlas writes through backend tools, so no client mutation runs and
      // nothing here invalidates on its own. Without this, the page the chat
      // sits beside keeps showing the rows it fetched when it mounted — an
      // edit made mid-conversation is invisible there, "Unsaved changes — not
      // live yet" included, and so is the version a deploy just minted.
      onWrite: (scopes) => {
        invalidateAgenticLogicWrites(queryClient, scopes);
      },
      onScenarioEvaluated: ({ ruleId, scenarioName, request, response }) => {
        // Seed, do not refetch: this response IS the evaluation the workspace
        // would otherwise have had to ask for, and asking again would run the
        // rule a second time for the same answer. The canvas reads this cache
        // entry — it never calls the route itself.
        queryClient.setQueryData(
          agenticLogicKeys.scenarioEvaluation(ruleId, scenarioName),
          response
        );
        dispatch(
          setAiChat({
            key: "activeScenario",
            value: {
              ruleId,
              name: scenarioName,
              runToken: (activeScenarioRef.current?.runToken ?? 0) + 1,
              ...(request ? { request } : {}),
            },
          })
        );
      },
      onSendFailed: (error) => {
        // eslint-disable-next-line no-console
        console.error("[AgenticLogicCreationChat] send failed", error);
        dispatch(markChatInterrupted());
      },
      stopChat,
      stopStream: chatWorkflow.stop,
    });
  }

  // Async because opening a session ON A RULE has to carry that rule's current
  // source and graph: without them the authoring node can only re-derive the
  // whole file from the rule's name and description, quietly dropping every
  // filter and ordering the merchant never asked about. The draft is normally
  // already in the react-query cache on the workspace, so this awaits nothing;
  // when it is not, awaiting is the point.
  const startSession = async (payload: ChatWorkflowRequestBodyDto) => {
    if (!payload.input) {
      return;
    }
    // Idle chat (the always-visible panel with no active build): the first
    // message itself starts a code-entry session, the same one the create
    // page's "Build" starts. Stored so the create page locks its composer
    // and follow-up turns reuse the entry.
    let context = initialContext;
    if (!context && !aiChat.sessionId) {
      // A typed message and a clicked offer (a cart to run) both open a
      // session; only an input carrying no prose at all cannot.
      const opening = readOpeningPrompt(payload.input);
      if (!opening) {
        return;
      }
      // This turn IS the session's first message; the auto-start effect
      // must not replay `initialInput` on top of it. Set BEFORE the await.
      autoStarted.current = true;
      // The rail is the same chat on the create page and on a rule's
      // workspace. Opening from a rule means this message is about THAT
      // rule — refining it, or running a cart against it — not a brief for
      // a second one.
      const view = resolveAgenticLogicView(
        locationRef.current.search,
        currentSurface().ruleParam
      );
      setOpeningSession(() => true);
      try {
        context = await buildCodeEntryCreationContext({
          prompt: opening.prompt,
          files: opening.files,
          existingRuleId: view.kind === "rule" ? view.ruleId : undefined,
        });
      } finally {
        // Cleared here rather than after `send`: the stream's `isPending`
        // takes over the instant `send` runs, in the same render.
        setOpeningSession(() => false);
      }
      dispatch(
        setAiChat([
          { key: "initialContext", value: context },
          { key: "start", value: true },
          { key: "buildingRuleId", value: null },
          { key: "createdRuleId", value: null },
          { key: "activeScenario", value: null },
        ])
      );
    }
    // No context here means a follow-up on a session this rail already has.
    controller.current?.send(payload, {
      initialContext: context ?? null,
      sessionId: aiChat.sessionId,
    });
  };

  const isSending = chatWorkflow.isPending || openingSession;

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      // `OnAiChatSend.chat` is sync; the session start is not. A rejected
      // start leaves the session unstarted on purpose — a refused edit is
      // recoverable, an edit sent without the rule's source is a silent full
      // rewrite of the merchant's rule.
      void startSession(payload).catch((error: unknown) => {
        autoStarted.current = false;
        // eslint-disable-next-line no-console
        console.error("[AgenticLogicCreationChat] session start failed", error);
      });
    },
    stop: () => cancelActiveAgenticLogicCreation("user"),
    resume: (sessionId) => controller.current?.resume(sessionId),
    isPending: isSending,
  };

  // The new-rule conversation survives a reload. Once its session exists the
  // creation view names it in the URL (`?rule=new&session=<id>`, replacing
  // the entry rather than adding one), and `useAtlasChatRail` restores that
  // session from the server's history when the page loads on it. Only the
  // creation view does this — a rule's conversation is found through the
  // rule itself.
  const view = resolveAgenticLogicView(
    location.search,
    currentSurface().ruleParam,
    currentSurface().sessionParam
  );
  const isOnCreationView = view.kind === "new";

  // A conversation restored from history streams nothing, so a rule its turn
  // created while no page was attached (a reload mid-create) never reached the
  // hand-offs above: the view stays on the new-rule skeleton and the rule is
  // never linked to the conversation that wrote it. The saved transcript
  // records the same tool calls, so the hand-off runs from it. Only on the
  // new-rule view: a rule's own page restores its authoring transcript too,
  // and is already where the hand-off would lead.
  useEffect(() => {
    const sessionId = aiChat.sessionId;
    if (!sessionId || isSending || !isOnCreationView) {
      return;
    }
    const restored = trackAgenticLogicCreationInTranscript(aiChat.turns);
    const stage = handedOff.current.get(sessionId);
    if (restored.completedRule && stage !== "completed") {
      handOffCompletedRule(restored.completedRule, sessionId);
    } else if (restored.createdCodeRuleId && !stage) {
      handOffCreatedRule(restored.createdCodeRuleId, sessionId);
    }
  }, [aiChat.sessionId, aiChat.turns, isOnCreationView, isSending]);

  const unrecordedSessionId =
    isOnCreationView &&
    initialContext &&
    aiChat.sessionId &&
    view.sessionId !== aiChat.sessionId
      ? aiChat.sessionId
      : null;
  useEffect(() => {
    if (!unrecordedSessionId) {
      return;
    }
    navigate(currentSurface().newRuleSessionPath(unrecordedSessionId), {
      replace: true,
      state: locationRef.current.state,
    });
  }, [navigate, unrecordedSessionId]);

  // What a reload or a tab close would still throw away: the conversation is
  // restorable, but a rule is what the merchant came for, and until one
  // exists the answers they have given live only in an unfinished session.
  // Ends the moment the rule exists — its creation moves the page off the
  // creation view — and in-app navigation is never blocked by it: the
  // create → rule hand-off is a search change.
  const hasAnswers = aiChat.turns.some((turn) =>
    turn.nodes.some((node) =>
      node.messages.some((message) => message.role === "user")
    )
  );
  useBeforeUnloadGuard(
    isOnCreationView && Boolean(initialContext) && hasAnswers
  );

  useEffect(() => {
    if (!initialContext || aiChat.sessionId || autoStarted.current) {
      return;
    }
    autoStarted.current = true;
    onSend.chat({ sessionId: v4(), input: initialContext.initialInput });
  }, [aiChat.sessionId, initialContext, onSend]);

  const consumedRuleReview = useRef<typeof pendingRuleReview>(null);
  useEffect(() => {
    if (
      !pendingRuleReview ||
      isSending ||
      consumedRuleReview.current === pendingRuleReview
    ) {
      return;
    }
    const view = resolveAgenticLogicView(
      locationRef.current.search,
      currentSurface().ruleParam
    );
    if (view.kind !== "rule" || view.ruleId !== pendingRuleReview.ruleId) {
      dispatch(setAiChat([{ key: "pendingRuleReview", value: null }]));
      return;
    }
    consumedRuleReview.current = pendingRuleReview;
    dispatch(setAiChat([{ key: "pendingRuleReview", value: null }]));
    onSend.chat({
      sessionId: aiChat.sessionId ?? v4(),
      input: {
        type: "choice",
        choice: pendingRuleReview.content,
        label: "Fix the failed checks for this rule.",
      },
    });
  }, [aiChat.sessionId, dispatch, isSending, onSend, pendingRuleReview]);

  // Resets to the idle chat in place — the panel stays open (it is always
  // visible on the agentic-logic surfaces) and only the session behind it is
  // torn down. Clearing `initialContext` is what unlocks the create page.
  const startNewChat = () => {
    cancelActiveAgenticLogicCreation("new-chat");
    autoStarted.current = false;
    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "createdRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
    // The dismissed session must not come back on the next reload.
    if (isOnCreationView && view.sessionId) {
      navigate(currentSurface().newRulePath, {
        replace: true,
        state: locationRef.current.state,
      });
    }
  };

  // The rule this conversation is about, for the feedback under each reply.
  const feedbackRuleId =
    view.kind === "rule" ? view.ruleId : (buildingRuleId ?? undefined);

  return (
    <RailComposerCardProvider>
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        <DiscardChangesDialog
          open={confirmingExit}
          mode="leave"
          entityLabel="rule"
          leaveSubtitle="This rule has not been created yet. You can leave and start again anytime."
          onKeepEditing={() => setConfirmingExit(() => false)}
          onDiscard={() => {
            setConfirmingExit(() => false);
            // Leaving the creation view is what ends the session and closes
            // the panel (`LoggedInProvider`), the same bail-out as the
            // designing canvas's "All rules".
            navigate(currentSurface().exitPath);
          }}
        />
        <ExecutionPanel
          sx={{ flex: 1, minHeight: 0 }}
          closeLabel="Close Atlas"
          // Figma "Atlas / Rail header" (516:3173): the 32px brand tile with an
          // 18px sparkle (the 5/9 default), 12px before the 16px/24px semibold
          // wordmark, hairline divider under the row.
          title={
            <>
              <IgAiIcon size={32} surface="tile" sx={{ mr: 3 }} shine />
              <IgTypography variant="h3" sx={{ fontWeight: 600 }}>
                Atlas
              </IgTypography>
            </>
          }
          feature="other"
          onClose={onClose}
          onNewChat={startNewChat}
          onSend={onSend}
          workflowId={WORKFLOW_IDS.AGENTIC_LOGIC_RULE_CREATION}
          placeholder="Describe what the rule should do…"
          showSystemMessages={false}
          showToolCallOutput={false}
          showToolsAsTasks={true}
          showToolsAsSteps={true}
          clearReasoningOnMessage={true}
          showReasoningMessages={false}
          showConditionNodes={false}
          reasoningNodeAllowList={["llm-chat"]}
          conversationTools={ATLAS_CONVERSATION_TOOLS}
          slots={{
            header: (
              <AtlasRailHeader
                onExitChat={
                  layout === "full" ? () => setConfirmingExit(() => true) : null
                }
                onHideRail={railCollapse.canHide ? railCollapse.hide : null}
              />
            ),
            // `CartScenarioAsk` steps aside for any parked question, so it never
            // fights the survey card the panel itself renders; the chips step
            // aside for either card (`railComposerCard`).
            composerAsk: <CartScenarioAsk />,
            toolCard: renderTemplateRuleProposalCard,
            composerSuggestions: <AtlasRailSuggestions />,
            turnFooter: ({ turn, streaming }) => (
              <AtlasTurnFeedback
                turn={turn}
                streaming={streaming}
                sessionId={aiChat.sessionId}
                ruleId={feedbackRuleId}
              />
            ),
          }}
        />
      </Stack>
    </RailComposerCardProvider>
  );
};
